import React, { useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import classNames from 'classnames';

import {
  SelectOption,
  SelectValue,
} from '../../../core/components/form-item/form-item-types';
import Loader from '../../../core/components/loader/loader';
import { useIsMobile } from '../../../core/hooks/use-is-mobile';
import { useReportInitialLoad } from '../../../core/hooks/use-report-initial-load';
import { AppComponent } from '../../../core/types/core-types';

import { getEmployees } from './employee-api';
import { Employee } from './employee-types';

const EmployeeContact: AppComponent<{
  sorting?: SelectValue<SelectOption<string>>;
  selectedEmployees?: string[];
  selectedDivisions?: string[];
  selectedDepartments?: string[];
}> = ({
  InnerHTML,
  sorting,
  selectedEmployees,
  selectedDivisions,
  selectedDepartments,
}) => {
  const isMobile = useIsMobile();

  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState<Employee[]>();

  const reportInitialLoad = useReportInitialLoad();

  useEffect(() => {
    const getEmployeesList = async () => {
      try {
        setLoading(true);

        const result = await getEmployees({
          ...(selectedEmployees && sorting?.value === 'employees'
            ? { employees: selectedEmployees }
            : {}),
          ...(selectedDivisions && sorting?.value === 'divisions'
            ? { divisions: selectedDivisions }
            : {}),
          ...(selectedDepartments && sorting?.value === 'departments'
            ? { departments: selectedDepartments }
            : {}),
        });

        reportInitialLoad();

        unstable_batchedUpdates(() => {
          setEmployees(result);
          setLoading(false);
        });
      } catch (e) {
        console.error(e);
        unstable_batchedUpdates(() => {
          setEmployees([]);
          setLoading(false);
        });
      }
    };

    getEmployeesList();
  }, [
    reportInitialLoad,
    selectedDepartments,
    selectedDivisions,
    selectedEmployees,
    sorting?.value,
  ]);

  return employees !== undefined ? (
    <>
      {loading && <Loader />}

      <div className="employee-contact">
        {employees.map(
          ({
            id,
            title_before,
            first_name,
            last_name,
            title_after,
            function: fun,
            office,
            phonenumber1,
            phonenumber2,
            email,
            division,
            department,
          }) => (
            <div className="employee-contact__item" key={id}>
              <h3 className="employee-contact__name">
                {!!title_before && (
                  <span className="employee-contact__title-before">
                    {title_before}
                  </span>
                )}
                <div className="employee-contact__name-part">
                  <span>{first_name}</span>
                  <span>{last_name}</span>
                </div>
                {!!title_after && (
                  <span className="employee-contact__title-after">
                    {title_after}
                  </span>
                )}
              </h3>
              {!isMobile ? (
                <p
                  className={classNames(
                    'employee-contact__position',
                    !!fun && 'nomargin'
                  )}
                >
                  {division?.name}
                  {!!department && ', '}
                  {department?.name}
                </p>
              ) : (
                <div
                  className={classNames(
                    'employee-contact__position',
                    'flex flex--column',
                    !!fun && 'nomargin'
                  )}
                >
                  <p className="nomargin">{division?.name}</p>
                  <p className="nomargin">{department?.name}</p>
                </div>
              )}
              {!!fun && <p className="employee-contact__function">{fun}</p>}
              {!!email && (
                <span>
                  <a href={`mailto:${email}`}>{email}</a>
                </span>
              )}
              {!!(phonenumber1 || phonenumber2) && (
                <div className="employee-contact__numbers">
                  {!!phonenumber1 && (
                    <a href={`tel:${phonenumber1}`}>{phonenumber1}</a>
                  )}
                  {!!phonenumber2 && (
                    <a href={`tel:${phonenumber2}`}>{phonenumber2}</a>
                  )}
                </div>
              )}
              <p>Miestnosť: {office}</p>
            </div>
          )
        )}
      </div>
    </>
  ) : (
    <InnerHTML />
  );
};

export default EmployeeContact;
